import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import React from "react";

import { FlatRow, InterviewerRow, RoundStats } from "@/types/job";

export type Props = {
  data: InterviewerRow[];
};

const QDRCell: React.FC<{ stats: RoundStats }> = ({ stats }) => (
  <div className="flex items-center gap-1">
    <span>{stats.qualified}</span>
    <span className="text-gray-300">|</span>
    <span>{stats.rejected}</span>
    <span className="text-gray-300">|</span>
    <span>{stats.dropped}</span>
  </div>
);

const InterviewerAnalyticsTable: React.FC<Props> = ({ data }) => {
  const allRounds = React.useMemo(() => {
    const map = new Map<number, string>();
    data.forEach((row) => {
      row.round_breakdown.forEach((r) => map.set(r.round_id, r.round_name));
    });
    return Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
  }, [data]);

  const flatData = React.useMemo<FlatRow[]>(() => {
    return data.map((row) => {
      const flat: FlatRow = {
        interviewer_id: row.interviewer_id,
        interviewer_name: row.interviewer_name,
        total_interviewed: row.total_interviewed,
        submitted_to_client: row.submitted_to_client,
        offered: row.offered,
      };
      row.round_breakdown.forEach((r) => {
        flat[`round__${r.round_name}`] = {
          qualified: r.qualified,
          rejected: r.rejected,
          dropped: r.dropped,
        };
      });
      return flat;
    });
  }, [data]);

  const [sorting, setSorting] = React.useState<SortingState>([
    { id: "offered", desc: true },
  ]);

  const columns = React.useMemo<ColumnDef<FlatRow>[]>(() => {
    const fixed: ColumnDef<FlatRow>[] = [
      {
        id: "interviewer_name",
        header: "Interviewer",
        accessorKey: "interviewer_name",
        enableSorting: true,
        cell: ({ getValue }) => (
          <span className="font-medium text-gray-900">
            {getValue() as string}
          </span>
        ),
      },
      {
        id: "total_interviewed",
        header: "Total Interviewed",
        accessorKey: "total_interviewed",
        enableSorting: true,
      },
      {
        id: "submitted_to_client",
        header: "Submitted to Client",
        accessorKey: "submitted_to_client",
        enableSorting: true,
      },
    ];

    const roundCols: ColumnDef<FlatRow>[] = allRounds.map(([, roundName]) => ({
      id: `round__${roundName}`,
      header: `${roundName} - (Q|R|D)`,
      accessorKey: `round__${roundName}`,
      enableSorting: false,
      cell: ({ getValue }) => {
        const stats = getValue() as RoundStats | undefined;
        if (!stats) return <span className="text-gray-300">—</span>;
        return <QDRCell stats={stats} />;
      },
    }));

    const offerCol: ColumnDef<FlatRow> = {
      id: "offered",
      header: "Offered",
      accessorKey: "offered",
      enableSorting: true,
    };

    return [...fixed, ...roundCols, offerCol];
  }, [allRounds]);

  const table = useReactTable({
    data: flatData,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    filterFns: {},
  });

  return (
    <div className="my-2 max-h-[420px] w-full overflow-x-auto overflow-y-auto rounded border">
      <table className="min-w-[1000px] border-collapse">
        <thead className="bg-slate-100">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className={`sticky top-0 min-w-[140px] border bg-slate-100 px-3 py-2 text-left text-xs font-semibold text-gray-700 ${
                    header.column.id === "interviewer_name"
                      ? "left-0 z-30 shadow-[2px_0_4px_rgba(0,0,0,0.08)]"
                      : "z-20"
                  }`}
                >
                  <div
                    className={`flex items-center gap-1 ${
                      header.column.getCanSort()
                        ? "cursor-pointer select-none"
                        : ""
                    }`}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                    {header.column.getIsSorted() === "asc" && " ▲"}
                    {header.column.getIsSorted() === "desc" && " ▼"}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className="hover:bg-slate-50">
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  className={`border px-3 py-2 text-sm text-gray-800 ${
                    cell.column.id === "interviewer_name"
                      ? "sticky left-0 z-10 bg-white shadow-[2px_0_4px_rgba(0,0,0,0.08)]"
                      : ""
                  }`}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default InterviewerAnalyticsTable;
